// <Card> — Material 3 card primitive.
//
// Per GOOGLE_DESIGN_PATTERN_ANALYSIS.md §"Cards Pattern": tonal container,
// rounded corners, optional header / footer / action row.
// One primary CTA per action row (DESIGN_POLICY.md §5).

import type { ReactNode } from 'react';
import { tokens } from '../tokens';

export interface CardProps {
  header?: ReactNode;
  /** Tonal level (0..3). Defaults to 1 (containerLow). */
  level?: 0 | 1 | 2 | 3;
  children: ReactNode;
  footer?: ReactNode;
  /** Action row (typically buttons). One primary CTA only. */
  actions?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function Card({ header, level = 1, children, footer, actions, className, 'data-testid': testId }: CardProps) {
  const surfaceLevel: 0 | 1 | 2 | 3 = level;
  return (
    <div
      data-testid={testId}
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: tokens.color.surface[surfaceLevel],
        color: tokens.color.ink.primary,
        borderRadius: tokens.radius.lg,
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        overflow: 'hidden',
      }}
    >
      {header ? (
        <div
          style={{
            padding: tokens.space[3],
            borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.lg,
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          {header}
        </div>
      ) : null}
      <div style={{ padding: tokens.space[3], flex: '1 1 auto' }}>{children}</div>
      {footer ? (
        <div
          style={{
            padding: tokens.space[3],
            borderTop: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            color: tokens.color.ink.secondary,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.engMeta.size,
          }}
        >
          {footer}
        </div>
      ) : null}
      {actions ? (
        <div
          style={{
            padding: tokens.space[3],
            display: 'flex',
            gap: tokens.space[2],
            justifyContent: 'flex-end',
            borderTop: footer ? undefined : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
          }}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}
